import React, { useState, useEffect, useRef } from "react";
import {
  XMarkIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PencilIcon,
  EyeIcon,
  PaperClipIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { TaskFormBuilderCompact } from "../forms/TaskFormBuilderCompact";
import { FormLibrary } from "../forms/FormLibrary";
import { FormViewer } from "../forms/FormViewer";
import { GoogleDriveFilePicker } from "./GoogleDriveFilePicker";
import {
  TaskFormData,
  CustomNodeData,
  Form,
  ActivityDefinition,
  GoogleDriveAttachment,
} from "../../types";
import { apiService } from "../../services/apiService";
import toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

interface TaskFormPanelProps {
  nodeData: CustomNodeData;
  nodeId: string;
  onClose: () => void;
  onFormSave: (
    nodeId: string,
    formData: TaskFormData,
    updatedNodeData?: Partial<CustomNodeData>
  ) => void;
  readonly?: boolean;
  viewMode?: "edit" | "view";
  projectId?: number;
}

export const TaskFormPanel: React.FC<TaskFormPanelProps> = ({
  nodeData,
  nodeId,
  onClose,
  onFormSave,
  readonly = false,
  viewMode = "edit",
  projectId,
}) => {
  const { t } = useTranslation(["workflows", "common", "forms", "activities"]);
  const [activityForms, setActivityForms] = useState<Form[]>([]);
  const [selectedFormIndex, setSelectedFormIndex] = useState<number>(0);
  const [showFormLibrary, setShowFormLibrary] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formViewMode, setFormViewMode] = useState<"edit" | "view">("edit");

  // Track if we've initialized the data to avoid overwriting user input
  const hasInitialized = useRef(false);

  // Helper function to check if a form is a default empty form
  const isDefaultEmptyForm = (form: Form) => {
    return (
      (!form.title || form.title.trim() === "") &&
      (!form.description || form.description.trim() === "") &&
      form.formStructure.elements.length === 0 &&
      form.id < 0 // Temporary ID indicates it's a new/default form
    );
  };

  // Activity basic info - prioritize taskFormData if available
  const [activityName, setActivityName] = useState(
    nodeData.taskFormData?.activityName ||
      nodeData.taskFormData?.formName ||
      nodeData.label ||
      ""
  );
  const [activityDescription, setActivityDescription] = useState(
    nodeData.taskFormData?.activityDescription ||
      nodeData.taskFormData?.formDescription ||
      nodeData.description ||
      ""
  );
  const [startDate, setStartDate] = useState(
    nodeData.taskFormData?.startDate || ""
  );
  const [endDate, setEndDate] = useState(nodeData.taskFormData?.endDate || "");
  const [assignedUserIds, setAssignedUserIds] = useState<number[]>(
    nodeData.taskFormData?.assignedUserIds || []
  );
  const [requiresApproval, setRequiresApproval] = useState(
    nodeData.approvalRequired ??
      nodeData.activityDefinition?.requiresApproval ??
      false
  );
  const [isLeave, setIsLeave] = useState<boolean>(
    Boolean(
      nodeData.taskFormData?.isLeave ?? nodeData.activityDefinition?.isLeave ?? false
    )
  );
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<GoogleDriveAttachment[]>(
    nodeData.taskFormData?.attachments || []
  );
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [showDateFields, setShowDateFields] = useState(
    !!(nodeData.taskFormData?.startDate || nodeData.taskFormData?.endDate)
  );

  // Step management - always start with basic step for both edit and view modes
  const [currentStep, setCurrentStep] = useState<"basic" | "forms">("basic");

  // Initialize with existing form data or create a default form
  useEffect(() => {
    // Only initialize once to avoid overwriting user input
    if (hasInitialized.current) return;

    // Update activity name and description when nodeData changes (only on first load)
    if (nodeData.taskFormData?.activityName) {
      setActivityName(nodeData.taskFormData.activityName);
    }
    if (nodeData.taskFormData?.activityDescription) {
      setActivityDescription(nodeData.taskFormData.activityDescription);
    }

    // Function to load pool activity forms (regular activities)
    const loadPoolActivityForms = async (activityId: number) => {
      try {
        const activity = await apiService.getActivityById(activityId);

        if (activity.activityForms && activity.activityForms.length > 0) {
          const loadedForms: Form[] = activity.activityForms.map(
            (activityForm: any) => ({
              id: activityForm.form.id,
              title: activityForm.form.title,
              description: activityForm.form.description || "",
              formStructure: activityForm.form.formStructure,
              submissionData: activityForm.form.submissionData || {}, // ✅ Include submission data!
              createdBy: activityForm.form.createdBy,
              isPublic: activityForm.form.isPublic,
              isArchived: activityForm.form.isArchived,
              version: activityForm.form.version,
              createdAt: activityForm.form.createdAt,
              updatedAt: activityForm.form.updatedAt,
            })
          );

          setActivityForms(loadedForms);
          setActivityName(activity.title);
          setActivityDescription(activity.description || "");
          setIsLeave(Boolean(activity.isLeave));
        } else {
        }
      } catch (error) {}
    };

    if (nodeData.taskFormData && activityForms.length === 0) {
      // Check if we have multiple forms data (new format)
      if (
        nodeData.taskFormData.multipleFormsData &&
        nodeData.taskFormData.multipleFormsData.length > 0
      ) {
        const restoredForms: Form[] =
          nodeData.taskFormData.multipleFormsData.map((formData, index) => {
            const restoredForm = {
              id: formData.id,
              title: formData.title,
              description: formData.description || "",
              formStructure: formData.formStructure,
              submissionData: formData.submissionData || {}, // Restore user's input data
              createdBy: 1,
              isPublic: false,
              isArchived: false,
              version: 1,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };

            return restoredForm;
          });

        setActivityForms(restoredForms);

        // Load activity basic info if available
        if (nodeData.taskFormData.activityName) {
          setActivityName(nodeData.taskFormData.activityName);
        }
        if (nodeData.taskFormData.activityDescription) {
          setActivityDescription(nodeData.taskFormData.activityDescription);
        }
      } else if (
        nodeData.taskFormData.multipleFormsData &&
        nodeData.taskFormData.multipleFormsData.length === 0
      ) {
        // Activity has no forms - don't create any default forms
        setActivityForms([]);

        // Load activity basic info if available
        if (nodeData.taskFormData.activityName) {
          setActivityName(nodeData.taskFormData.activityName);
        }
        if (nodeData.taskFormData.activityDescription) {
          setActivityDescription(nodeData.taskFormData.activityDescription);
        }
      } else {
        // Convert legacy single TaskFormData to new Form format
        const legacyForm: Form = {
          id: -Date.now(), // ✅ Negative ID for temp forms
          title: nodeData.taskFormData.formName || "New Form",
          description: nodeData.taskFormData.formDescription || "",
          formStructure: {
            elements: nodeData.taskFormData.elements || [],
            elementOrder: JSON.parse(
              nodeData.taskFormData.elementOrder || "[]"
            ).map((id: any) => String(id)),
          },
          createdBy: 1, // Current user
          isPublic: false,
          isArchived: false,
          version: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        setActivityForms([legacyForm]);
      }
    } else if (
      !nodeData.taskFormData &&
      (nodeData.activityDefinitionId || nodeData.activityDefinition?.id) &&
      activityForms.length === 0
    ) {
      // ✅ Pool activity: Load forms from activity by ID
      const poolActivityId =
        nodeData.activityDefinitionId || nodeData.activityDefinition?.id;

      if (poolActivityId) {
        loadPoolActivityForms(poolActivityId);
      }
    } else {
    }

    // Mark as initialized after first load
    hasInitialized.current = true;
  }, [nodeData, activityForms.length]);

  // 🔄 Fresh data reload for existing activities
  useEffect(() => {
    const reloadFreshData = async () => {
      // Check if this is an existing activity that needs fresh data
      const existingActivityId =
        nodeData.taskFormData?.activityId ||
        nodeData.activityDefinitionId ||
        nodeData.activityDefinition?.id;

      if (
        existingActivityId &&
        existingActivityId > 0 &&
        hasInitialized.current
      ) {
        try {
          const freshActivity = await apiService.getActivityById(
            existingActivityId
          );
          setRequiresApproval(Boolean(freshActivity.requiresApproval));
          setIsLeave(Boolean(freshActivity.isLeave));

          if (
            freshActivity.activityForms &&
            freshActivity.activityForms.length > 0
          ) {
            const freshForms: Form[] = freshActivity.activityForms.map(
              (activityForm: any) => ({
                id: activityForm.form.id,
                title: activityForm.form.title,
                description: activityForm.form.description || "",
                formStructure: activityForm.form.formStructure,
                submissionData: activityForm.form.submissionData || {},
                createdBy: activityForm.form.createdBy,
                isPublic: activityForm.form.isPublic,
                isArchived: activityForm.form.isArchived || false,
                version: activityForm.form.version,
                createdAt: activityForm.form.createdAt,
                updatedAt: activityForm.form.updatedAt,
              })
            );

            setActivityForms(freshForms);
          }
        } catch (error) {}
      }
    };

    reloadFreshData();
  }, [
    nodeData.taskFormData?.activityId,
    nodeData.activityDefinitionId,
    nodeData.activityDefinition?.id,
  ]);

  // Load project members when projectId is available
  useEffect(() => {
    const loadProjectMembers = async () => {
      if (projectId) {
        try {
          const members = await apiService.getWorkflowContainerMembers(
            projectId
          );
          setProjectMembers(members);
        } catch (error) {
          setProjectMembers([]);
        }
      }
    };

    loadProjectMembers();
  }, [projectId]);

  // 🔍 DEBUG: Log current state

  const handleFormChange = (newFormData: TaskFormData) => {
    if (selectedFormIndex >= 0 && selectedFormIndex < activityForms.length) {
      const currentForm = activityForms[selectedFormIndex];
      const oldElements = currentForm.formStructure.elements || [];
      const newElements = newFormData.elements || [];

      // ✅ CRITICAL FIX: Map submission data when element IDs change
      let mappedSubmissionData = { ...newFormData.submissionData } || {};

      // Check if element IDs have changed and need mapping
      if (oldElements.length > 0 && newElements.length > 0) {
        // Create mapping between old and new elements based on label and type
        const elementMapping: Record<string, string> = {};

        oldElements.forEach((oldElement, index) => {
          const newElement =
            newElements.find(
              (newEl) =>
                newEl.label === oldElement.label &&
                newEl.type === oldElement.type
            ) || newElements[index]; // Fallback to position-based mapping

          if (newElement && oldElement.id !== newElement.id) {
            elementMapping[oldElement.id] = newElement.id;
          }
        });

        // Apply mapping to submission data
        if (Object.keys(elementMapping).length > 0) {
          const oldSubmissionData = currentForm.submissionData || {};
          mappedSubmissionData = { ...newFormData.submissionData };

          Object.entries(elementMapping).forEach(([oldId, newId]) => {
            if (oldSubmissionData[oldId] !== undefined) {
              mappedSubmissionData[newId] = oldSubmissionData[oldId];
            }
          });
        }
      }

      const updatedForms = [...activityForms];
      updatedForms[selectedFormIndex] = {
        ...updatedForms[selectedFormIndex],
        title: newFormData.formName,
        description: newFormData.formDescription || "",
        formStructure: {
          elements: newFormData.elements,
          elementOrder: JSON.parse(newFormData.elementOrder || "[]"),
        },
        submissionData: mappedSubmissionData, // Use mapped submission data
        updatedAt: new Date().toISOString(),
      };

      setActivityForms(updatedForms);
      setIsDirty(true);
    }
  };

  const handleAddForm = (form?: Form) => {
    if (form) {
      // Add existing form from library
      setActivityForms((prev) => {
        // Eğer sadece 1 form var ve o da default boş form ise, replace et
        if (prev.length === 1 && isDefaultEmptyForm(prev[0])) {
          return [form]; // Replace the default form
        } else {
          return [...prev, form]; // Add to existing forms
        }
      });
    } else {
      // Create new blank form
      const newForm: Form = {
        id: -Date.now() - Math.random(), // ✅ Negative ID for temp forms
        title: "",
        description: "",
        formStructure: {
          elements: [],
          elementOrder: [],
        },
        createdBy: 1,
        isPublic: false,
        isArchived: false,
        version: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setActivityForms((prev) => [...prev, newForm]);
      setSelectedFormIndex(activityForms.length); // Select the new form
    }
    setIsDirty(true);
    setShowFormLibrary(false);
  };

  const handleRemoveForm = (index: number) => {
    if (activityForms.length <= 1) {
      toast.error(t("workflows:activityMustHaveAtLeastOneForm"));
      return;
    }

    if (window.confirm(t("workflows:confirmRemoveForm"))) {
      const newForms = activityForms.filter((_, i) => i !== index);
      setActivityForms(newForms);

      // Adjust selected form index
      if (selectedFormIndex >= newForms.length) {
        setSelectedFormIndex(Math.max(0, newForms.length - 1));
      } else if (selectedFormIndex > index) {
        setSelectedFormIndex(selectedFormIndex - 1);
      }

      setIsDirty(true);
    }
  };

  const handleMoveForm = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= activityForms.length) return;

    const newForms = [...activityForms];
    [newForms[index], newForms[newIndex]] = [
      newForms[newIndex],
      newForms[index],
    ];
    setActivityForms(newForms);

    // Update selected index if needed
    if (selectedFormIndex === index) {
      setSelectedFormIndex(newIndex);
    } else if (selectedFormIndex === newIndex) {
      setSelectedFormIndex(index);
    }

    setIsDirty(true);
  };

  const handleFilesSelected = (files: GoogleDriveAttachment[]) => {
    setAttachments([...attachments, ...files]);
    setIsDirty(true);
    toast.success(t("workflows:filesAdded", { count: files.length }));
  };

  const handleRemoveAttachment = (id: string) => {
    if (window.confirm(t("workflows:confirmRemoveFile"))) {
      setAttachments(attachments.filter((att) => att.id !== id));
      setIsDirty(true);
      toast.success(t("workflows:fileRemoved"));
    }
  };

  const handleSave = async () => {
    if (!activityName.trim()) {
      toast.error(t("workflows:activityNameRequired"));
      return;
    }

    // Validate all forms have titles (only if forms exist)
    if (activityForms.length > 0) {
      for (let i = 0; i < activityForms.length; i++) {
        if (!activityForms[i].title || !activityForms[i].title.trim()) {
          toast.error(
            `Form ${
              i + 1
            } title is required. Please enter a title for all forms.`
          );
          return;
        }
      }
    }

    // Determine if we're in edit mode (existing activity) or create mode (new activity)
    const isEditMode = !!(
      nodeData.taskFormData?.activityId ||
      nodeData.activityDefinitionId ||
      nodeData.activityDefinition?.id
    );

    if (isEditMode) {
      await handleEditActivity();
    } else {
      await handleCreateActivity();
    }
  };

  const handleCreateActivity = async () => {
    try {
      // 🏊 POOL ACTIVITY CHECK: If this is from Activity Library, forms are already saved
      const isFromActivityLibrary = !!(
        nodeData.activityDefinitionId || nodeData.activityDefinition?.id
      );

      if (isFromActivityLibrary) {
        // For library activities, keep immediate save behavior
        const loadingToast = toast.loading(t("workflows:updatingActivity"));
        const existingActivityId =
          nodeData.activityDefinitionId || nodeData.activityDefinition?.id;

        if (!existingActivityId) {
          toast.dismiss(loadingToast);
          toast.error(t("workflows:noActivityIdFound"));
          return;
        }

        // Update existing activity with new name/description
        const activityData = {
          title: activityName.trim(),
          description: activityDescription.trim(),
          isLeave: isLeave,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          assignedUsers: JSON.stringify(assignedUserIds),
        };

        try {
          await apiService.updateActivity(existingActivityId, activityData);

          // Fetch fresh activity data
          const freshActivity = await apiService.getActivityById(
            existingActivityId
          );

          // Update node data
          const updatedTaskFormData: TaskFormData = {
            activityName: activityName.trim(),
            activityDescription: activityDescription.trim(),
            activityId: existingActivityId,
            isLeave: isLeave,
            formName: activityName.trim(),
            formDescription: activityDescription.trim(),
            elements: [],
            elementOrder: "[]",
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            assignedUserIds: assignedUserIds,
            attachments: attachments,
            multipleFormsData: freshActivity.activityForms
              ? freshActivity.activityForms.map(
                  (activityForm: any, index: number) => ({
                    id: activityForm.form.id,
                    title: activityForm.form.title,
                    description: activityForm.form.description || "",
                    formStructure: activityForm.form.formStructure,
                    submissionData: activityForm.form.submissionData || {},
                    displayOrder: activityForm.displayOrder || index,
                    isFromLibrary: true,
                  })
                )
              : [],
          };

          const updatedNodeData = {
            ...nodeData,
            label: activityName.trim(),
            description: activityDescription.trim(),
            approvalRequired: requiresApproval,
            taskFormData: updatedTaskFormData,
            activityDefinition: freshActivity,
            activityDefinitionId: freshActivity.id,
          };

          onFormSave(nodeId, updatedTaskFormData, updatedNodeData);
          toast.dismiss(loadingToast);
          toast.success(
            t("workflows:activityUpdatedSuccessfully", {
              name: activityName.trim(),
            })
          );
          return;
        } catch (error) {
          toast.dismiss(loadingToast);
          toast.error(t("workflows:activityUpdateFailed"));
          return;
        }
      }

      // 🆕 NEW BEHAVIOR: For new activities, save as pending (no database operations)
      toast.success(t("workflows:activitySavedAsPending"));

      // Prepare pending forms data (keep all forms in their current state)
      const pendingFormsData = activityForms.map((form, index) => ({
        id: form.id,
        title: form.title,
        description: form.description || "",
        formStructure: form.formStructure,
        submissionData: form.submissionData || {},
        displayOrder: index,
        isFromLibrary: false,
      }));

      // Prepare task form data for the node
      const nodeTaskFormData: TaskFormData = {
        activityName: activityName.trim(),
        activityDescription: activityDescription.trim(),
        formName: activityName.trim(),
        formDescription: activityDescription.trim(),
        elements: [],
        elementOrder: "[]",
        isLeave: isLeave,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        assignedUserIds: assignedUserIds,
        attachments: attachments,
        multipleFormsData: pendingFormsData,
      };

      // Update node with pending flag
      const updatedNodeData = {
        ...nodeData,
        label: activityName.trim(),
        description: activityDescription.trim(),
        taskFormData: nodeTaskFormData,
        approvalRequired: requiresApproval,
        isPendingActivity: true, // 🔥 Mark as pending
        pendingForms: activityForms, // 🔥 Store forms to be saved later
      };

      onFormSave(nodeId, nodeTaskFormData, updatedNodeData);
      onClose(); // Close panel after successful save
    } catch (error) {
      toast.error(t("workflows:activitySaveFailed"));
    }
  };

  const handleEditActivity = async () => {
    try {
      // Get existing activity ID
      const existingActivityId =
        nodeData.taskFormData?.activityId ||
        nodeData.activityDefinitionId ||
        nodeData.activityDefinition?.id;

      // 🆕 Check if this is a pending activity (not yet in database)
      if (nodeData.isPendingActivity) {
        // For pending activities, just update local state (no API calls)
        toast.success(t("workflows:activityUpdatedLocally"));

        // Prepare pending forms data
        const pendingFormsData = activityForms.map((form, index) => ({
          id: form.id,
          title: form.title,
          description: form.description || "",
          formStructure: form.formStructure,
          submissionData: form.submissionData || {},
          displayOrder: index,
          isFromLibrary: false,
        }));

        // Prepare task form data for the node
        const updatedTaskFormData: TaskFormData = {
          activityName: activityName.trim(),
          activityDescription: activityDescription.trim(),
          formName: activityName.trim(),
          formDescription: activityDescription.trim(),
          elements: [],
          elementOrder: "[]",
          isLeave: isLeave,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          assignedUserIds: assignedUserIds,
          attachments: attachments,
          multipleFormsData: pendingFormsData,
        };

        // Update node data while keeping pending flag
        const updatedNodeData = {
          ...nodeData,
          label: activityName.trim(),
          description: activityDescription.trim(),
          approvalRequired: requiresApproval,
          taskFormData: updatedTaskFormData,
          isPendingActivity: true,
          pendingForms: activityForms,
        };

        onFormSave(nodeId, updatedTaskFormData, updatedNodeData);
        onClose(); // Close panel after successful save
        return;
      }

      // For existing activities in database, keep immediate update behavior
      const loadingToast = toast.loading(t("workflows:updatingActivity"));

      if (!existingActivityId) {
        toast.dismiss(loadingToast);
        toast.error(t("workflows:noExistingActivityId"));
        return;
      }

      // ✅ CRITICAL FIX: Ensure ALL forms are saved before updating activity
      // Check if any forms are unsaved (negative IDs = temp forms)
      const unsavedForms = activityForms.filter(
        (form) => !form.id || form.id < 0
      );

      if (unsavedForms.length > 0) {
        // Save all forms first
        const savedForms = await handleSaveFormsOnly();
        if (!savedForms) {
          toast.dismiss(loadingToast);
          toast.error(t("workflows:failedToSaveForms"));
          return;
        }

        // Use the fresh saved forms for activity update
        const activityData: Partial<ActivityDefinition> = {
          title: activityName.trim(),
          description: activityDescription.trim(),
          isPublic: false,
          requiresApproval: requiresApproval,
          isLeave: isLeave,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          assignedUsers: JSON.stringify(assignedUserIds), // Fixed: Use assignedUsers string format
          attachments:
            attachments.length > 0 ? JSON.stringify(attachments) : undefined, // ✅ Include attachments
          // ✅ Use freshly saved forms (all have positive IDs now)
          activityForms: savedForms.map((form, index) => ({
            id: 0, // Will be set by backend for new relationships
            form: form,
            displayOrder: index,
            isRequired: true,
            isVisible: true,
            conditionExpression: undefined,
          })),
        };

        // Update the activity
        const updatedActivity = await apiService.updateActivity(
          existingActivityId,
          activityData
        );
      } else {
        // All forms are already saved, proceed normally
        const activityData: Partial<ActivityDefinition> = {
          title: activityName.trim(),
          description: activityDescription.trim(),
          isPublic: false,
          requiresApproval: requiresApproval,
          isLeave: isLeave,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          assignedUsers: JSON.stringify(assignedUserIds), // Fixed: Use assignedUsers string format
          attachments:
            attachments.length > 0 ? JSON.stringify(attachments) : undefined, // ✅ Include attachments
          // ✅ Include current activity forms to maintain the relationships
          activityForms: activityForms.map((form, index) => ({
            id: 0, // Will be set by backend for new relationships
            form: form,
            displayOrder: index,
            isRequired: true,
            isVisible: true,
            conditionExpression: undefined,
          })),
        };

        // Update the activity
        const updatedActivity = await apiService.updateActivity(
          existingActivityId,
          activityData
        );
      }

      // 🔄 Fetch fresh activity data from database to get updated form relationships
      const freshActivity = await apiService.getActivityById(
        existingActivityId
      );

      // ✅ Parse attachments from fresh activity (in case they were updated by backend)
      let freshAttachments = attachments;
      if (freshActivity.attachments) {
        try {
          freshAttachments = JSON.parse(freshActivity.attachments);
        } catch (e) {
        }
      }

      // ✅ FIX: Update node data with COMPLETE fresh activity info including updated forms
      const updatedTaskFormData: TaskFormData = {
        activityName: activityName.trim(),
        activityDescription: activityDescription.trim(),
        activityId: existingActivityId,
        isLeave: isLeave,
        formName: activityName.trim(), // For backward compatibility
        formDescription: activityDescription.trim(),
        elements: [], // Legacy field
        elementOrder: "[]", // Legacy field
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        assignedUserIds: assignedUserIds,
        attachments: freshAttachments, // ✅ Use fresh attachments from backend
        // ✅ CRITICAL FIX: Update multipleFormsData with fresh form data from database
        multipleFormsData: freshActivity.activityForms
          ? freshActivity.activityForms.map(
              (activityForm: any, index: number) => ({
                id: activityForm.form.id,
                title: activityForm.form.title,
                description: activityForm.form.description || "",
                formStructure: activityForm.form.formStructure,
                submissionData: activityForm.form.submissionData || {},
                displayOrder: activityForm.displayOrder || index,
                isFromLibrary: false,
              })
            )
          : [],
      };

      // Update nodeData with fresh activity definition to reflect new form count
      const updatedNodeData = {
        ...nodeData,
        taskFormData: updatedTaskFormData,
        activityDefinition: freshActivity,
        activityDefinitionId: freshActivity.id,
      };

      onFormSave(nodeId, updatedTaskFormData, updatedNodeData);

      toast.dismiss(loadingToast);
      toast.success(
        t("workflows:activityUpdatedSuccessfully", {
          name: activityName.trim(),
        })
      );
      onClose(); // Close panel after successful update
    } catch (error) {
      toast.error(t("workflows:activitySaveFailed"));
    }
  };

  const handleSaveFormsOnly = async (): Promise<Form[] | null> => {
    try {
      // Validate all forms have titles
      for (let i = 0; i < activityForms.length; i++) {
        if (!activityForms[i].title || !activityForms[i].title.trim()) {
          toast.error(
            t("forms:formNumberTitleRequired", { formNumber: i + 1 })
          );
          return null;
        }
      }

      // 🆕 Check if this is a pending activity or pool activity
      const isFromActivityLibrary = !!(
        nodeData.activityDefinitionId || nodeData.activityDefinition?.id
      );

      // For pending activities or new activities, just update local state (no API calls)
      if (nodeData.isPendingActivity || !isFromActivityLibrary) {
        toast.success(t("workflows:formsSavedLocally"));

        // Prepare pending forms data
        const pendingFormsData = activityForms.map((form, index) => ({
          id: form.id,
          title: form.title,
          description: form.description || "",
          formStructure: form.formStructure,
          submissionData: form.submissionData || {},
          displayOrder: index,
          isFromLibrary: false,
        }));

        // Update node data
        const currentTaskFormData: TaskFormData = {
          activityName: activityName.trim(),
          activityDescription: activityDescription.trim(),
          activityId: nodeData.taskFormData?.activityId,
          formName: activityName.trim(),
          formDescription: activityDescription.trim(),
          elements: [],
          elementOrder: "[]",
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          assignedUserIds: assignedUserIds,
          attachments: attachments,
          multipleFormsData: pendingFormsData,
        };

        const updatedNodeData = {
          ...nodeData,
          taskFormData: currentTaskFormData,
          approvalRequired: requiresApproval,
          isPendingActivity: true,
          pendingForms: activityForms,
        };

        onFormSave(nodeId, currentTaskFormData, updatedNodeData);

        // Go back to basic step
        setCurrentStep("basic");
        setIsDirty(false);

        return activityForms;
      }

      // For pool activities, keep immediate save behavior
      const loadingToast = toast.loading(t("workflows:savingForms"));

      let savedCount = 0;
      let updatedCount = 0;
      const updatedFormsList = [...activityForms];

      for (let i = 0; i < activityForms.length; i++) {
        const form = activityForms[i];
        const isExistingForm =
          form.id &&
          typeof form.id === "number" &&
          form.id > 0 &&
          Number.isInteger(form.id);

        if (isExistingForm) {
          // Update existing form
          try {
            const updateData = {
              title: form.title,
              description: form.description || "",
              formStructure: form.formStructure,
              submissionData: form.submissionData || {},
            };

            const updatedForm = await apiService.updateForm(
              form.id,
              updateData
            );
            updatedFormsList[i] = updatedForm;
            updatedCount++;
          } catch (error) {
            toast.dismiss(loadingToast);
            toast.error(
              `Failed to update form "${form.title}". Please try again.`
            );
            return null;
          }
        } else {
          // Create new form
          try {
            const createData = {
              title: form.title || `Form ${i + 1}`,
              description: form.description || "",
              formStructure: form.formStructure,
              submissionData: form.submissionData || {},
              isPublic: false,
              version: 1,
            };

            const savedForm = await apiService.createForm(createData);
            updatedFormsList[i] = savedForm;
            savedCount++;
          } catch (error) {
            toast.dismiss(loadingToast);
            toast.error(
              `Failed to save new form "${form.title}". Please try again.`
            );
            return null;
          }
        }
      }

      setActivityForms(updatedFormsList);

      const currentTaskFormData: TaskFormData = {
        activityName: activityName.trim(),
        activityDescription: activityDescription.trim(),
        activityId:
          nodeData.taskFormData?.activityId ||
          nodeData.activityDefinitionId ||
          nodeData.activityDefinition?.id,
        formName: activityName.trim(),
        formDescription: activityDescription.trim(),
        elements: [],
        elementOrder: "[]",
        attachments: attachments,
        multipleFormsData: updatedFormsList.map((form, index) => ({
          id: form.id,
          title: form.title,
          description: form.description || "",
          formStructure: form.formStructure,
          submissionData: form.submissionData || {},
          displayOrder: index,
          isFromLibrary: false,
        })),
      };

      onFormSave(nodeId, currentTaskFormData);

      toast.dismiss(loadingToast);

      if (savedCount > 0 && updatedCount > 0) {
        toast.success(
          t("workflows:formsCreatedAndUpdated", { savedCount, updatedCount })
        );
      } else if (savedCount > 0) {
        toast.success(t("workflows:formsCreated", { count: savedCount }));
      } else if (updatedCount > 0) {
        toast.success(t("workflows:formsUpdated", { count: updatedCount }));
      } else {
        toast.success(t("workflows:allFormsUpToDate"));
      }

      setCurrentStep("basic");
      setIsDirty(false);

      return updatedFormsList;
    } catch (error) {
      toast.error(t("workflows:activitySaveFailed"));
      return null;
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Step handlers
  const handleProceedToForms = () => {
    if (!activityName.trim()) {
      toast.error(t("workflows:activityNameRequired"));
      return;
    }
    setCurrentStep("forms");
    setIsDirty(true);
  };

  const handleBackToBasic = () => {
    setCurrentStep("basic");
  };

  // Get current form for editing
  const currentForm = activityForms[selectedFormIndex];
  const currentFormData: TaskFormData = currentForm
    ? {
        id: currentForm.id, // ✅ Include form ID!
        formName: currentForm.title,
        formDescription: currentForm.description || "",
        elements: currentForm.formStructure.elements,
        elementOrder: JSON.stringify(currentForm.formStructure.elementOrder),
        submissionData: currentForm.submissionData || {}, // Include user's input data
      }
    : {
        formName: "New Form",
        formDescription: "",
        elements: [],
        elementOrder: "[]",
        submissionData: {},
      };

  // Render basic info step
  const renderBasicInfoStep = () => (
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-[95vw] lg:max-w-5xl 2xl:max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <span className="text-2xl">🏷️</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {viewMode === "view"
                  ? t("workflows:viewActivity")
                  : t("workflows:editActivity")}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {t("activities:description")}
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Info Message for Managers */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <span className="text-2xl">💡</span>
              </div>
              <div>
                <p className="text-sm text-blue-800 font-medium">
                  {t("activities:durationInfoMessage")}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-8">
            {/* Left Column - Basic Info & Attachments (Actually Attachments should be on Right based on ActivityEditor change?) */}
            {/* Wait, user asked to match ActivityEditor layout: Left=Basic+Users, Right=Forms(Top)+Attachments(Bottom) */}
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("activities:basicInformation")}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("workflows:activityName")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={activityName}
                      onChange={(e) => {
                        setActivityName(e.target.value);
                        setIsDirty(true);
                      }}
                      placeholder={t("workflows:enterActivityName")}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={readonly || viewMode === "view"}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("activities:description")}
                    </label>
                    <textarea
                      value={activityDescription}
                      onChange={(e) => {
                        setActivityDescription(e.target.value);
                        setIsDirty(true);
                      }}
                      placeholder={t("workflows:describeActivity")}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      disabled={readonly || viewMode === "view"}
                    />
                  </div>

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={requiresApproval}
                        onChange={(e) => {
                          setRequiresApproval(e.target.checked);
                          setIsDirty(true);
                        }}
                        className="rounded"
                        disabled={readonly || viewMode === "view"}
                      />
                      <span className="text-sm">{t("workflows:approvalRequired")}</span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isLeave}
                        onChange={(e) => {
                          setIsLeave(e.target.checked);
                          setIsDirty(true);
                        }}
                        className="rounded"
                        disabled={readonly || viewMode === "view"}
                      />
                      <span className="text-sm">
                        Bu bir izin aktivitesidir. (Çalışılan saatlerden çıkarılır)
                      </span>
                    </label>
                  </div>

                  {!showDateFields ? (
                    <button
                      type="button"
                      onClick={() => setShowDateFields(true)}
                      disabled={readonly || viewMode === "view"}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {t("activities:addDates")}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">
                          {t("activities:dates")}
                        </label>
                        {!(readonly || viewMode === "view") && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowDateFields(false);
                              setStartDate("");
                              setEndDate("");
                              setIsDirty(true);
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            {t("activities:removeDates")}
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("activities:startDate")}
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => {
                              setStartDate(e.target.value);
                              setIsDirty(true);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={readonly || viewMode === "view"}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("activities:endDate")}
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => {
                              setEndDate(e.target.value);
                              setIsDirty(true);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={readonly || viewMode === "view"}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("activities:assignedUsers")}
                    </label>
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
                      {projectMembers.map((member) => {
                        const userId = member.userId || member.user?.id;
                        const isSelected = assignedUserIds.includes(userId);
                        return (
                          <label
                            key={member.id}
                            className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAssignedUserIds([
                                    ...assignedUserIds,
                                    userId,
                                  ]);
                                } else {
                                  setAssignedUserIds(
                                    assignedUserIds.filter(
                                      (id) => id !== userId
                                    )
                                  );
                                }
                                setIsDirty(true);
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              disabled={readonly || viewMode === "view"}
                            />
                            <span className="text-sm text-gray-700">
                              {member.user?.firstName} {member.user?.lastName} (
                              {member.role})
                            </span>
                          </label>
                        );
                      })}
                      {projectMembers.length === 0 && (
                        <p className="text-sm text-gray-500">
                          No project members available
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Forms (Top) & Attachments (Bottom) */}
            <div className="space-y-6">
              {/* Forms Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Forms ({activityForms.length})
                  </h3>
                  {viewMode === "edit" && (
                    <button
                      onClick={() => {
                        const newForm: Form = {
                          id: -Date.now(),
                          title: "",
                          description: "",
                          formStructure: {
                            elements: [],
                            elementOrder: [],
                          },
                          createdBy: 1,
                          isPublic: false,
                          isArchived: false,
                          version: 1,
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString(),
                        };

                        const newForms = [...activityForms, newForm];
                        setActivityForms(newForms);
                        setSelectedFormIndex(newForms.length - 1);

                        setFormViewMode("edit");
                        setCurrentStep("forms");
                        setIsDirty(true);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <span>+</span>
                      <span>Form Ekle</span>
                    </button>
                  )}
                </div>

                <div className="space-y-2 max-h-64 overflow-y-auto mb-6">
                  {activityForms.length === 0 ? (
                    <div className="text-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <DocumentTextIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No forms added yet</p>
                      <p className="text-xs text-gray-400">
                        Click "Form Ekle" to add your first form
                      </p>
                    </div>
                  ) : (
                    activityForms.map((form, index) => (
                      <div
                        key={form.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      >
                        <div className="flex items-center space-x-3">
                          <DocumentTextIcon className="w-5 h-5 text-blue-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {form.title || `Form ${index + 1}`}
                            </p>
                            <p className="text-xs text-gray-500">
                              {form.formStructure.elements.length} fields
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          {viewMode === "edit" && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedFormIndex(index);
                                  setFormViewMode("edit");
                                  setCurrentStep("forms");
                                }}
                                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                title={t("activities:editForm")}
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  const newForms = activityForms.filter(
                                    (_, i) => i !== index
                                  );
                                  setActivityForms(newForms);
                                  setIsDirty(true);
                                }}
                                className="p-1 text-red-600 hover:bg-red-100 rounded"
                                title={t("activities:removeForm")}
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {viewMode === "view" && (
                            <button
                              onClick={() => {
                                setSelectedFormIndex(index);
                                setFormViewMode("view");
                                setCurrentStep("forms");
                              }}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                              title={t("activities:viewForm")}
                            >
                              <EyeIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Attachments Section - Updated UI */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-base font-semibold text-gray-800 flex items-center space-x-2">
                    <PaperClipIcon className="w-5 h-5 text-green-600" />
                    <span>{t("activities:attachedDocuments")}</span>
                  </label>
                  {viewMode === "edit" && (
                    <button
                      type="button"
                      onClick={() => setShowDrivePicker(true)}
                      disabled={readonly}
                      className="flex items-center space-x-2 text-sm text-green-600 hover:text-green-700 font-medium bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span>{t("activities:addFromGoogleDrive")}</span>
                    </button>
                  )}
                </div>

                {viewMode === "edit" && !readonly && (
                  <div className="flex items-center justify-center w-full mb-4">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-green-300 border-dashed rounded-xl cursor-pointer bg-green-50 hover:bg-green-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg
                          className="w-8 h-8 mb-3 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">
                            {t("activities:clickToUpload") || "Dosya yüklemek için tıklayın"}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("activities:uploadFileTypes") || "PDF, DOC, XLS, JPG, PNG"}
                        </p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        onChange={async (e) => {
                          if (e.target.files && e.target.files[0]) {
                            const file = e.target.files[0];
                            const loadingToast = toast.loading(
                              t("activities:uploading")
                            );
                            try {
                              const uploadedFile =
                                await apiService.uploadTemplateFile(file);

                              const newAttachment: GoogleDriveAttachment = {
                                id: uploadedFile.id,
                                name: uploadedFile.name,
                                url: uploadedFile.url,
                                mimeType: uploadedFile.mimeType,
                                iconUrl: uploadedFile.iconUrl,
                                thumbnailUrl: uploadedFile.thumbnailUrl,
                                addedAt: new Date().toISOString(),
                              };

                              handleFilesSelected([newAttachment]);
                              toast.success(t("activities:uploadSuccess"), {
                                id: loadingToast,
                              });
                            } catch (error) {
                              toast.error(t("activities:uploadError"), {
                                id: loadingToast,
                              });
                            }
                          }
                        }}
                      />
                    </label>
                  </div>
                )}

                {attachments.length > 0 ? (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      {t("activities:attachedFiles")} ({attachments.length})
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {attachments.map((att) => (
                        <div
                          key={att.id}
                          className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200 hover:border-green-300 hover:shadow-sm transition-all"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            {att.iconUrl ? (
                              <img
                                src={att.iconUrl}
                                alt=""
                                className="w-8 h-8 object-contain"
                              />
                            ) : (
                              <DocumentTextIcon className="w-8 h-8 text-blue-500" />
                            )}
                            <div className="flex-1 min-w-0">
                              <button
                                type="button"
                                onClick={() => {
                                  const previewWindow =
                                    apiService.shouldOpenPreviewWindow(att.name, att.mimeType)
                                      ? window.open("", "_blank")
                                      : null;
                                  if (previewWindow) {
                                    previewWindow.opener = null;
                                    apiService.preparePreviewWindow(previewWindow, att.name);
                                  }
                                  void apiService
                                    .previewGoogleDriveFile(att.id, att.name, previewWindow)
                                    .catch(() => toast.error("Dosya görüntülenemedi."));
                                }}
                                className="text-sm font-medium text-blue-600 hover:underline truncate block text-left"
                              >
                                {att.name}
                              </button>
                              <div className="flex items-center space-x-2 text-xs text-gray-500">
                                <span>
                                  {t("activities:added")}:{" "}
                                  {new Date(att.addedAt).toLocaleDateString(
                                    "tr-TR"
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                          {viewMode === "edit" && !readonly && (
                            <button
                              onClick={() => handleRemoveAttachment(att.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-2"
                              title={t("activities:removeAttachment")}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  (readonly || viewMode === "view") && (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <DocumentTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">
                        {t("activities:noDocumentsAttachedYet")}
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Only show in edit mode */}
      {viewMode === "edit" && (
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {nodeData.isPendingActivity && (
                <span className="flex items-center text-blue-600 text-sm font-medium">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-2 animate-pulse"></span>
                  ⏳ Pending - Will be saved when workflow is saved
                </span>
              )}
              {isDirty && !nodeData.isPendingActivity && (
                <span className="flex items-center text-amber-600 text-sm">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mr-2"></span>
                  You have unsaved changes
                </span>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!activityName.trim()}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span>💾</span>
                <span>
                  {nodeData.isPendingActivity
                    ? "Aktiviteyi Güncelle (Pending)"
                    : "Aktiviteyi Kaydet"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render forms step (existing design)
  const renderFormsStep = () => (
    <>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[95vw] lg:max-w-5xl 2xl:max-w-7xl h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleBackToBasic}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded-lg transition-colors mr-2"
                title={t("activities:backToActivityInfo")}
              >
                <span className="text-lg">←</span>
              </button>
              <div className="p-2 bg-blue-100 rounded-lg">
                <DocumentTextIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {activityName ||
                    (viewMode === "view"
                      ? t("activities:activityDetails")
                      : t("activities:activityForms"))}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {viewMode === "view"
                    ? t("activities:viewActivityFormsDescription")
                    : t("activities:addConfigureFormsDescription")}
                </p>
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Forms Tabs */}
        <div className="bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex space-x-2 overflow-x-auto">
              {activityForms.map((form, index) => (
                <button
                  key={form.id}
                  onClick={() => setSelectedFormIndex(index)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all duration-200 ${
                    selectedFormIndex === index
                      ? "bg-blue-500 text-white shadow-md"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <span>📝</span>
                    <span>{form.title || "New Form"}</span>
                    {activityForms.length > 1 && (
                      <span className="text-xs opacity-75">
                        {index + 1}/{activityForms.length}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>

            {!readonly && viewMode === "edit" && formViewMode === "edit" && (
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => handleAddForm()}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors"
                  title="Add new form"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>New Form</span>
                </button>
                <button
                  onClick={() => setShowFormLibrary(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
                  title="Add from library"
                >
                  <span>📚</span>
                  <span>Form Library</span>
                </button>
              </div>
            )}
          </div>

          {/* Form Controls */}
          {!readonly &&
            viewMode === "edit" &&
            formViewMode === "edit" &&
            activityForms.length > 0 && (
              <div className="px-6 pb-4 flex items-center justify-between bg-gray-50">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 font-medium">
                    Form Order:
                  </span>
                  <button
                    onClick={() => handleMoveForm(selectedFormIndex, "up")}
                    disabled={selectedFormIndex === 0}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    title="Move form up"
                  >
                    <ChevronUpIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleMoveForm(selectedFormIndex, "down")}
                    disabled={selectedFormIndex === activityForms.length - 1}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    title="Move form down"
                  >
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>
                </div>

                <button
                  onClick={() => handleRemoveForm(selectedFormIndex)}
                  disabled={activityForms.length <= 1}
                  className="flex items-center space-x-2 p-2 text-red-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                  title={t("activities:removeForm")}
                >
                  <TrashIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {t("activities:removeForm")}
                  </span>
                </button>
              </div>
            )}
        </div>

        {/* Current Form Editor */}
        <div className="flex-1 overflow-hidden bg-gray-50">
          {currentForm ? (
            <div className="h-full bg-white m-4 rounded-lg shadow-sm border border-gray-200">
              {formViewMode === "view" ? (
                <FormViewer
                  key={`form-viewer-${selectedFormIndex}-${currentForm?.id}`}
                  formData={currentFormData}
                  title={currentForm.title || `Form ${selectedFormIndex + 1}`}
                />
              ) : (
                <TaskFormBuilderCompact
                  key={`form-${selectedFormIndex}-${currentForm?.id}`}
                  initialData={currentFormData}
                  onSave={handleFormChange}
                  readonly={readonly || viewMode === "view"}
                  isEditMode={true}
                />
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8 bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <DocumentTextIcon className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {t("activities:noFormsConfigured")}
                </h3>
                <p className="text-gray-500 mb-6">
                  {viewMode === "view"
                    ? t("activities:noFormsConfiguredDescription")
                    : t("activities:createFirstFormDescription")}
                </p>
                {!readonly &&
                  viewMode === "edit" &&
                  formViewMode === "edit" && (
                    <div className="flex items-center justify-center space-x-3">
                      <button
                        onClick={() => handleAddForm()}
                        className="flex items-center space-x-2 px-6 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        <PlusIcon className="w-5 h-5" />
                        <span>{t("activities:createNewForm")}</span>
                      </button>
                      <button
                        onClick={() => setShowFormLibrary(true)}
                        className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <span>📚</span>
                        <span>{t("activities:chooseFromFormLibrary")}</span>
                      </button>
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>

        {/* Footer - Only show in edit mode */}
        {viewMode === "edit" && (
          <div className="p-6 border-t border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {nodeData.isPendingActivity && (
                  <span className="flex items-center text-blue-600 font-medium">
                    <span className="w-2 h-2 bg-blue-400 rounded-full mr-2 animate-pulse"></span>
                    ⏳ Pending - Forms will be saved when workflow is saved
                  </span>
                )}
                {isDirty && !nodeData.isPendingActivity && (
                  <span className="flex items-center text-amber-600">
                    <span className="w-2 h-2 bg-amber-400 rounded-full mr-2"></span>
                    You have unsaved changes
                  </span>
                )}
                {!isDirty &&
                  !nodeData.isPendingActivity &&
                  activityForms.length > 0 && (
                    <span className="flex items-center text-green-600">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                      All changes saved
                    </span>
                  )}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCancel}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                {currentStep === "basic" ? (
                  <button
                    onClick={handleSave}
                    disabled={!isDirty || activityForms.length === 0}
                    className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span>💾</span>
                    <span>Aktiviteyi Kaydet</span>
                  </button>
                ) : (
                  <button
                    onClick={handleSaveFormsOnly}
                    disabled={activityForms.length === 0}
                    className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span>💾</span>
                    <span>
                      {nodeData.isPendingActivity
                        ? "Formları Güncelle (Pending)"
                        : "Formları Kaydet"}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Full Screen Modal Overlay */}
      <div className="fixed inset-0 bg-black/50 z-50 p-2 sm:p-4 md:p-6 overflow-y-auto flex items-start lg:items-center justify-center">
        {currentStep === "basic" ? renderBasicInfoStep() : renderFormsStep()}
      </div>

      {/* Form Library Modal */}
      {showFormLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-6xl h-5/6 flex flex-col shadow-2xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <span className="text-2xl">📚</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Form Library
                  </h3>
                  <p className="text-sm text-gray-600">
                    Choose a form to add to your activity
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowFormLibrary(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-6 bg-gray-50">
              <div className="h-full bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <FormLibrary
                  selectionMode={true}
                  onSelectForm={handleAddForm}
                  multiSelect={false}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Drive File Picker Modal */}
      <GoogleDriveFilePicker
        isOpen={showDrivePicker}
        onClose={() => setShowDrivePicker(false)}
        onSelect={handleFilesSelected}
      />
    </>
  );
};
